import { useMemo, useState } from "react";
import type { PaginationState } from "@tanstack/react-table";

import { pageCount } from "@/core/api/pagination";
import { FLASHPAY_ROLES, useRealmRoles } from "@/core/auth";
import { DataTableClar } from "@/core/components/shared/data-table";
import {
  ReportingGaugesDrawer,
  reportingEndpoints,
  useReportingGauges,
} from "@/features/reporting";
import { useTransactions } from "@/features/wallet/api/use-transactions";
import type { TransactionFilter } from "@/features/wallet/api/dto";
import {
  TransactionsFilters,
  transactionsColumns,
  type TransactionsFiltersValue,
} from "@/features/wallet/components";
import { useTransactionsTranslation } from "@/features/wallet/i18n/use-transactions-translation";

const GAUGE_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.SERVICE,
] as const;

const WALLET_STUCK_GAUGE = ["flashpay_wallet_stuck_awaiting_ledger"] as const;

/** Historique des transactions (`POST /Transactions/all`) — orchestration back-office. */
export function TransactionsPage() {
  const { t } = useTransactionsTranslation();
  const realmRoles = useRealmRoles();
  const canGauges = GAUGE_ROLES.some((role) => realmRoles.includes(role));
  const walletGauges = useReportingGauges("wallet", canGauges);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [filters, setFilters] = useState<TransactionsFiltersValue>({});

  const params = useMemo(() => {
    const filter: TransactionFilter = {};
    if (filters.status) filter.status = filters.status;
    if (filters.type) filter.type = filters.type;
    if (filters.sourceWalletId?.trim())
      filter.sourceWalletId = filters.sourceWalletId.trim();
    if (filters.destinationWalletId?.trim())
      filter.destinationWalletId = filters.destinationWalletId.trim();
    if (filters.productCode?.trim())
      filter.productCode = filters.productCode.trim();
    return {
      filter,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    };
  }, [filters, pagination]);

  const { data, isLoading, isFetching, error, refetch } = useTransactions(params);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("wallet.transactions.title")}
        </h1>
        <div className="flex flex-wrap items-center gap-3">
          {canGauges ? (
            <ReportingGaugesDrawer
              gauges={walletGauges.data ?? []}
              names={WALLET_STUCK_GAUGE}
              provenanceEndpoints={[reportingEndpoints.wallet.gauges]}
            />
          ) : null}
        </div>
      </div>

      <TransactionsFilters
        value={filters}
        onChange={(value) => {
          setFilters(value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
      />

      <DataTableClar
        data={data?.items ?? []}
        columnsClar={transactionsColumns}
        title={t("wallet.transactions.title")}
        exportFileName="wallet-transactions"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />
    </section>
  );
}
